import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { usePageTracking } from './hooks/usePageTracking';
import { useMetaTags } from './hooks/useMetaTags';
import { useTranslation } from 'react-i18next';
import HeroSection from './components/HeroSection';
import StatsSection from './components/StatsSection';
import FeatureCarousel from './components/FeatureCarousel';
import FeatureSections from './components/FeatureSections';
import Reviews from './components/Reviews';
import PricingSection from './components/PricingSection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import Dashboard from './components/Dashboard';
import PricingPage from './components/Pricing';
import FeaturesPage from './components/Features';
import ContactPage from './components/Contact';
import AboutPage from './components/About';
import Navigation from './components/Navigation';
import CTABanner from './components/CTABanner';
import QRPage from './components/QRPage';
import QRDemoPage from './components/QRDemoPage';
import ProcessLogs from './components/ProcessLogs';
import FeedbackDashboard from './components/FeedbackDashboard';
import PasswordProtected from './components/PasswordProtected';
import Subscriptions from './components/Subscriptions';
import DailyUsage from './components/DailyUsage';
import PromoCodes from './components/PromoCodes';
import LocaleRouter from './components/LocaleRouter';
import DownloadRedirect from './components/DownloadRedirect';
import LocalizedDownloadRedirect from './components/LocalizedDownloadRedirect';
import RedeemRedirect from './components/RedeemRedirect';
import CampaignRedirect from './components/CampaignRedirect';



// Home Page
const HomePage = () => (
  <div style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)' }}>
    <HeroSection />
    <StatsSection />
    <FeatureSections />
    <FeatureCarousel />
    <Reviews />
    <PricingSection />
    <CTABanner />
    <FAQSection />
    <Footer />
  </div>
);

// Terms Page
const TermsPage = () => (
  <>
    <TermsOfUse />
    <Footer />
  </>
);

// Privacy Page
const PrivacyPage = () => (
  <>
    <PrivacyPolicy />
    <Footer />
  </>
);

// English-only wrapper components for non-localized routes
const EnglishTermsPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);

  return (
    <>
      <TermsOfUse />
      <Footer />
    </>
  );
};

const EnglishPrivacyPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);

  return (
    <>
      <PrivacyPolicy />
      <Footer />
    </>
  );
};

const PricingFullPage = () => <><PricingPage /><Footer /></>;
const FeaturesFullPage = () => <><FeaturesPage /><Footer /></>;
const ContactFullPage = () => <><ContactPage /><Footer /></>;
const AboutFullPage = () => <><AboutPage /><Footer /></>;



// App Content Component (needs to be inside BrowserRouter to use hooks)
const AppContent = () => {
  usePageTracking();
  useMetaTags();

  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)' }}>
      <Navigation />
      <div style={{ paddingTop: '64px' }}>
      <Routes>
          {/* Default route redirects to language-specific route */}
          <Route path="/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          
          {/* Locale-based routes */}
          <Route path="/:locale" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/pricing" element={<LocaleRouter><PricingFullPage /></LocaleRouter>} />
          <Route path="/:locale/features" element={<LocaleRouter><FeaturesFullPage /></LocaleRouter>} />
          <Route path="/:locale/contact" element={<LocaleRouter><ContactFullPage /></LocaleRouter>} />
          <Route path="/:locale/about" element={<LocaleRouter><AboutFullPage /></LocaleRouter>} />
          <Route path="/:locale/terms" element={<LocaleRouter><TermsPage /></LocaleRouter>} />
          <Route path="/:locale/privacy" element={<LocaleRouter><PrivacyPage /></LocaleRouter>} />
          <Route path="/:locale/download" element={<LocalizedDownloadRedirect />} />
          
          {/* Non-localized routes (admin/dashboard) */}
          <Route path="/dashboard-metrics" element={<PasswordProtected><Dashboard /></PasswordProtected>} />
          <Route path="/dashboard-process-logs" element={<PasswordProtected><ProcessLogs /></PasswordProtected>} />
          <Route path="/dashboard-logs-feedback" element={<PasswordProtected><FeedbackDashboard /></PasswordProtected>} />
          <Route path="/dashboard-subscriptions" element={<PasswordProtected><Subscriptions /></PasswordProtected>} />
          <Route path="/dashboard-daily-usage" element={<PasswordProtected><DailyUsage /></PasswordProtected>} />
          <Route path="/dashboard-promo-codes" element={<PasswordProtected><PromoCodes /></PasswordProtected>} />
          <Route path="/qr" element={<QRPage />} />
          <Route path="/qrcode" element={<QRDemoPage />} />
          <Route path="/:locale/qr" element={<LocaleRouter><QRPage /></LocaleRouter>} />
          <Route path="/:locale/qrcode" element={<LocaleRouter><QRDemoPage /></LocaleRouter>} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/code/:code" element={<RedeemRedirect />} />
          <Route path="/c/:campaignId" element={<CampaignRedirect />} />
          
          {/* Non-localized legal pages (default to English) */}
          <Route path="/privacy" element={<EnglishPrivacyPage />} />
          <Route path="/terms" element={<EnglishTermsPage />} />
        </Routes>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;